import { useState, useEffect, useRef } from "react"
import { Link } from 'react-router-dom'

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const [isHidden, setIsHidden] = useState(false)
    const [isScrolled, setIsScrolled] = useState(false)
    const [showFollowModal, setShowFollowModal] = useState(false)
    const [followerCount, setFollowerCount] = useState(null)
    const [isHovering, setIsHovering] = useState(false)
    const lastScrollY = useRef(0)

    useEffect(() => {
        // Fetch follower counts from all platforms
        const fetchFollowers = async () => {
            try {
                const apiUrl = '/api/analytics/followers';
                
                const response = await fetch(apiUrl)
                const data = await response.json()
                
                if (!data.success) {
                    console.log('Failed to fetch followers')
                    return
                }
                
                let totalFollowers = 0
                
                // Add Facebook followers
                if (data.data?.facebook?.followers) {
                    totalFollowers += data.data.facebook.followers
                    console.log('Facebook followers:', data.data.facebook.followers)
                }
                
                // Add Instagram followers
                if (data.data?.instagram?.followers) {
                    totalFollowers += data.data.instagram.followers
                    console.log('Instagram followers:', data.data.instagram.followers)
                }
                
                console.log('Total followers:', totalFollowers)
                setFollowerCount(totalFollowers)
            } catch (error) {
                console.error('Error fetching followers:', error)
            }
        }
        
        fetchFollowers()
        // Refresh every 5 minutes
        const interval = setInterval(fetchFollowers, 5 * 60 * 1000)
        return () => clearInterval(interval)
    }, [])

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY
            
            // Add background when scrolled past threshold
            setIsScrolled(currentScrollY > 50)
            
            // Hide header when scrolling down, show when scrolling up
            if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
                // Scrolling down & past threshold - hide header
                setIsHidden(true)
            } else {
                // Scrolling up - show header
                setIsHidden(false)
            }
            
            lastScrollY.current = currentScrollY
        }

        window.addEventListener("scroll", handleScroll, { passive: true })
        return () => window.removeEventListener("scroll", handleScroll)
    }, [])

    const handleNavClick = () => {
        setIsMenuOpen(false)
    }

    const handleFollowClick = () => {
        setShowFollowModal(true)
    }

    const handleSocialAction = (action) => {
        const pageUrl = 'https://rdsstudio.co.za'
        const facebookPage = 'https://www.facebook.com/profile.php?id=61579996723546'
        const instagramPage = 'https://www.instagram.com/rdsstudio1111/'
        const linkedinPage = 'https://www.linkedin.com/in/jean-pierre-matthys-du-toit-7671493a3/?originalSubdomain=za'
        const youtubePage = 'https://www.youtube.com/channel/UCqEgC_kMwk4MqEcIC_yCayw'

        switch(action) {
            case 'facebook-like':
                window.open(facebookPage, '_blank', 'width=600,height=400')
                break
            case 'facebook-share':
                window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`, '_blank', 'width=600,height=400')
                break
            case 'instagram':
                window.open(instagramPage, '_blank')
                break
            case 'linkedin':
                window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`, '_blank', 'width=600,height=400')
                break
            case 'twitter':
                window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent('Check out RDS Studio - Web Design & Digital Marketing!')}`, '_blank', 'width=600,height=400')
                break
            case 'youtube':
                window.open(youtubePage, '_blank')
                break
            case 'whatsapp':
                window.open(`https://wa.me/?text=${encodeURIComponent('Check out RDS Studio - Web Design & Digital Marketing! ' + pageUrl)}`, '_blank')
                break
        }
        setShowFollowModal(false)
    }

    return (
        <>
            {/* Skip Navigation Link for Accessibility */}
            <a href="#main-content" className="skip-to-content" tabIndex="1">Skip to main content</a>
            
            {/* Main Header */}
            <header className={`sticky-header ${isScrolled ? "scrolled" : ""} ${isHidden ? "header-hidden" : ""}`} role="navigation" aria-label="Primary Navigation">
                <div className="header-container">
                    <div className="logo">
                        <Link to="/" className="logo-link">
                            <img 
                                src="/images/logo/RDS Logo.png"
                                srcSet="/images/logo/RDS Logo.png 1x, /images/logo/RDS Logo.png 2x"
                                sizes="80px"
                                alt="RDS Studio Logo" 
                                className="logo-image"
                                width="80"
                                height="24"
                                onError={(e) => {
                                    e.target.style.display = "none"
                                    e.target.nextElementSibling.style.display = "flex"
                                }}
                            />
                            <div className="logo-fallback" style={{ display: "none" }}>
                                <svg width="24" height="24" viewBox="0 0 40 40" fill="none">
                                    <path d="M8 32V8H20C24.4183 8 28 11.5817 28 16C28 20.4183 24.4183 24 20 24H12V32H8ZM12 12V20H20C22.2091 20 24 18.2091 24 16C24 13.7909 22.2091 12 20 12H12Z" fill="#2DD4BF"/>
                                    <path d="M32 32V8H36C38.2091 8 40 9.79086 40 12V28C40 30.2091 38.2091 32 36 32H32ZM36 12H32V28H36C37.1046 28 38 27.1046 38 26V14C38 12.8954 37.1046 12 36 12Z" fill="#14B8A6"/>
                                </svg>
                            </div>
                        </Link>
                        <div className="logo-text-header">
                            <div className="logo-text-main">Responsive Design Solutions</div>
                            <div className="logo-text-sub">STUDIO</div>
                        </div>
                    </div>
                    
                    <div className="header-right">
                        {/* Social Icons - Desktop only */}
                        <div className="social-icons-header">
                            <a href="https://www.facebook.com/profile.php?id=61579996723546" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                                <i className="fab fa-facebook-f"></i>
                            </a>
                            <a href="https://www.instagram.com/rdsstudio1111/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                                <i className="fab fa-instagram"></i>
                            </a>
                            <a href="https://www.linkedin.com/in/jean-pierre-matthys-du-toit-7671493a3/?originalSubdomain=za" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                                <i className="fab fa-linkedin-in"></i>
                            </a>
                            <a href="https://www.youtube.com/channel/UCqEgC_kMwk4MqEcIC_yCayw" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                                <i className="fab fa-youtube"></i>
                            </a>
                            <a href="https://x.com/rdsstudio1111" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)">
                                <i className="fab fa-x-twitter"></i>
                            </a>
                            <button 
                                className="follow-btn" 
                                onClick={handleFollowClick}
                                onMouseEnter={() => setIsHovering(true)}
                                onMouseLeave={() => setIsHovering(false)}
                                aria-label="Follow us on social media"
                            >
                                <i className="fas fa-heart"></i>
                                <span className="follower-text">
                                    {isHovering ? 'Follow' : followerCount === null ? '...' : followerCount.toLocaleString()}
                                </span>
                            </button>
                            <Link to="/seo-audit" className="nav-seo-link header-seo-pill">Free SEO Audit</Link>
                        </div>
                        
                        <nav
                            id="primary-navigation"
                            className={`nav-menu ${isMenuOpen ? "active" : ""}`}
                            aria-hidden={!isMenuOpen}
                        >
                        <ul>
                            <li><Link to="/" onClick={handleNavClick}>Home</Link></li>
                            <li><Link to="/about" onClick={handleNavClick}>About</Link></li>
                            <li><Link to="/services" onClick={handleNavClick}>Services</Link></li>
                            <li><Link to="/portfolio" onClick={handleNavClick}>Portfolio</Link></li>
                            <li><Link to="/contact" onClick={handleNavClick}>Contact</Link></li>
                            <li><Link to="/blog" onClick={handleNavClick}>Blog</Link></li>
                        </ul>
                        {/* Social icons inside mobile menu */}
                        <div className="social-icons-mobile">
                            <a href="https://www.facebook.com/profile.php?id=61579996723546" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                                <i className="fab fa-facebook-f"></i>
                            </a>
                            <a href="https://www.instagram.com/rdsstudio1111/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                                <i className="fab fa-instagram"></i>
                            </a>
                            <a href="https://www.linkedin.com/in/jean-pierre-matthys-du-toit-7671493a3/?originalSubdomain=za" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                                <i className="fab fa-linkedin-in"></i>
                            </a>
                            <a href="https://www.youtube.com/channel/UCqEgC_kMwk4MqEcIC_yCayw" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                                <i className="fab fa-youtube"></i>
                            </a>
                            <a href="https://x.com/rdsstudio1111" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)">
                                <i className="fab fa-x-twitter"></i>
                            </a>
                        </div>
                    </nav>
                    </div>
                
                    <button
                        type="button"
                        className={`hamburger ${isMenuOpen ? "open" : ""}`}
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        aria-label="Toggle navigation menu"
                        aria-controls="primary-navigation"
                        aria-expanded={isMenuOpen}
                    >
                        <span></span>
                        <span></span>
                        <span></span>
                    </button>
                </div>
            </header>

            {/* Follow Modal */}
            {showFollowModal && (
                <div className="follow-modal-overlay" onClick={() => setShowFollowModal(false)}>
                    <div className="follow-modal" onClick={(e) => e.stopPropagation()}>
                        <button className="close-modal" onClick={() => setShowFollowModal(false)} aria-label="Close">
                            <i className="fas fa-times"></i>
                        </button>
                        <h3>Follow & Share RDS Studio</h3>
                        <p>Connect with us and share our page!</p>
                        
                        <div className="follow-options">
                            <button className="follow-option facebook" onClick={() => handleSocialAction('facebook-like')}>
                                <i className="fab fa-facebook-f"></i>
                                <div>
                                    <strong>Like Page</strong>
                                    <span>Follow on Facebook</span>
                                </div>
                            </button>
                            
                            <button className="follow-option facebook-share" onClick={() => handleSocialAction('facebook-share')}>
                                <i className="fas fa-share-alt"></i>
                                <div>
                                    <strong>Share</strong>
                                    <span>Share on Facebook</span>
                                </div>
                            </button>
                            
                            <button className="follow-option instagram" onClick={() => handleSocialAction('instagram')}>
                                <i className="fab fa-instagram"></i>
                                <div>
                                    <strong>Follow</strong>
                                    <span>Follow on Instagram</span>
                                </div>
                            </button>
                            
                            <button className="follow-option linkedin" onClick={() => handleSocialAction('linkedin')}>
                                <i className="fab fa-linkedin-in"></i>
                                <div>
                                    <strong>Share</strong>
                                    <span>Share on LinkedIn</span>
                                </div>
                            </button>
                            
                            <button className="follow-option youtube" onClick={() => handleSocialAction('youtube')}>
                                <i className="fab fa-youtube"></i>
                                <div>
                                    <strong>Subscribe</strong>
                                    <span>Follow on YouTube</span>
                                </div>
                            </button>
                            
                            <button className="follow-option twitter" onClick={() => handleSocialAction('twitter')}>
                                <i className="fab fa-twitter"></i>
                                <div>
                                    <strong>Tweet</strong>
                                    <span>Share on Twitter</span>
                                </div>
                            </button>
                            
                            <button className="follow-option whatsapp" onClick={() => handleSocialAction('whatsapp')}>
                                <i className="fab fa-whatsapp"></i>
                                <div>
                                    <strong>Share</strong>
                                    <span>Share on WhatsApp</span>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default Header
